import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Input, Modal, Table, Button, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { triggerNotification } from "../../store/directActions";
import PageHeader from "../PageHeader";
import { onBarcodeScan } from "../../utils/barcodeEvents";
import { queryApi } from "../../store/actions/common";

const { Search } = Input;

// Dummy API functions - Replace these with actual API endpoints
const fetchScanMetaApi = async (scanCode) => {
  return queryApi(`ledger/scan_meta/${scanCode}`, "GET");
};

const addInventoryApi = async (data) => {
  return queryApi(`slot/inventory`, "POST", data);
};

function ModalInventory({ showModal, toggleModal, onSuccess }) {
  const [localData, setLocalData] = useState([]);
  const [scanCode, setScanCode] = useState("");
  const scanInputRef = useRef(null);
  const queryClient = useQueryClient();

  const focusScanInput = () => {
    // Delay lets the modal finish its open animation before focusing.
    setTimeout(() => {
      scanInputRef.current?.focus({ cursor: "end" });
    }, 100);
  };

  useEffect(() => {
    if (showModal) focusScanInput();
  }, [showModal]);

  // Mutation for fetching scan metadata
  const { mutate: fetchScanMeta, isPending: isScanning } = useMutation({
    mutationFn: (code) => fetchScanMetaApi(code),
    onSuccess: (scan_meta) => {
      console.log("scanned meta", scan_meta);

      if (!scan_meta.is_inventory && !scan_meta.is_active) {
        if (!localData.some((item) => item.pack === scan_meta.pack)) {
          if (scan_meta.amount) {
            setLocalData([scan_meta, ...localData]);
            setScanCode("");
          }
        } else {
          triggerNotification({
            title: "Error",
            description: "You have already added this pack no",
            type: "error",
          });
        }
      } else {
        console.log("show error not in inventory");
        if (scan_meta.is_inventory) {
          triggerNotification({
            title: "Error",
            description: "This pack is already in inventory",
            type: "error",
          });
        }
        if (scan_meta.is_active) {
          triggerNotification({
            title: "Error",
            description: "This pack is already active",
            type: "error",
          });
        }
      }
      // Keep the scan field ready for the next code.
      focusScanInput();
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to scan code",
        type: "error",
      });
      focusScanInput();
    },
  });

  // Mutation for adding inventory
  const { mutate: addInventory, isPending: isAdding } = useMutation({
    mutationFn: (data) => addInventoryApi(data),
    onSuccess: () => {
      setLocalData([]);
      toggleModal(false);
      queryClient.invalidateQueries({
        queryKey: ["slot/inventory"],
      });
      if (onSuccess) {
        onSuccess();
      }
      triggerNotification({
        title: "Success",
        description: "Inventory added successfully",
        type: "success",
      });
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to add inventory",
        type: "error",
      });
    },
  });

  const getSerchedData = (data) => {
    if (data && data.trim()) {
      fetchScanMeta(data);
    }
  };

  const onFormSubmit = () => {
    console.log("form data", localData);

    addInventory(
      localData.map((item) => {
        return { scan_code: item.scan_code };
      })
    );
  };

  const inventoryColumns = [
    {
      title: "#",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => <div>{index + 1}</div>,
      width: "48px",
    },
    {
      title: "Game",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => (
        <div>
          {record.name} - {record.game_number}
        </div>
      ),
      width: "160px",
      sorter: (a, b) => a.game_number - b.game_number,
      defaultSortOrder: "ascend",
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Pack #</div>,
      dataIndex: "pack",
      key: "pack",
      sorter: (a, b) => a.pack - b.pack,
      defaultSortOrder: "ascend",
      render: (item, record, index) => (
        <div>
          {record.pack}
          {/* <br></br>
          {record.pack_size} */}
        </div>
      ),
      width: 100,
    },
    {
      title: "Value",
      dataIndex: "amount",
      key: "amount",
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount}
        </div>
      ),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Total Value</div>,
      dataIndex: "totalPack",
      key: "totalPack",
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount * record.pack_size}
        </div>
      ),
      width: 150,
    },
    {
      title: "Action",
      key: "action",
      render: (item, record, index) => (
        <Space size="middle">
          <DeleteOutlined
            style={{ fontSize: 22, color: "red" }}
            onClick={() => {
              setLocalData(
                localData.filter((item, itemIndex) => itemIndex !== index)
              );
            }}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    return onBarcodeScan(async (barcode) => {
      if (showModal) getSerchedData(barcode);
    });
  }, [showModal]);

  return (
    <Modal
      width="80%"
      style={{ height: "80vh" }}
      maskClosable={false}
      title="Add Inventory"
      open={showModal}
      onOk={() => onFormSubmit()}
      onCancel={() => {
        setLocalData([]);
        toggleModal(false);
      }}
      okButtonProps={{
        disabled: localData?.length === 0,
        loading: isAdding,
      }}
      cancelButtonProps={{ disabled: isAdding }}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Search
            ref={scanInputRef}
            placeholder="enter ticket code, or scan"
            enterButton="Add"
            size="medium"
            allowClear
            value={scanCode}
            onChange={(e) => setScanCode(e.target.value)}
            onSearch={getSerchedData}
            loading={isScanning}
          />
        </Col>

        <Col span={24}>
          <PageHeader
            style={{ padding: 12 }}
            ghost={true}
            title={`${"$"}${localData.reduce(
              (value, item) => value + item.amount * item.pack_size,
              0
            )}`}
            subTitle={`Total pack : ${localData.length}`}
            extra={[
              <Button
                key="3"
                onClick={() => {
                  setLocalData([]);
                }}
                disabled={isAdding}
              >
                Clear
              </Button>,
            ]}
            wrapperClasses="mt-4"
          />
        </Col>
        <Col span={24}>
          <div style={{ overflowY: "auto" }}>
            <Table
              dataSource={localData}
              scroll={{ y: 280 }}
              bordered
              columns={inventoryColumns}
              pagination={false}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

export default ModalInventory;
